import { API_CONFIG } from '../config/api';
import {
  shouldSkipLoginRedirectForCalculator401,
  getAuth401DebugInfo,
  isApi401DebugEnabled,
} from '../config/embedFlags';

const LOG_401 = '[Bidmanager api:401]';

class DashboardService {
  constructor() {
    this.baseUrl = API_CONFIG.FULL_API_URL;
  }

  async apiRequest(url, options = {}) {
    const token = localStorage.getItem('token');
    
    options.headers = {
      ...options.headers,
      'Content-Type': 'application/json',
      'x-application-type': 'bidmanager-admin-panel'
    };

    // Add auth token if available
    if (token) {
      options.headers['Authorization'] = `Bearer ${token}`;
    }

    let response = await fetch(this.baseUrl + url, options);

    // Handle 401 - Try to refresh token for authenticated app requests only.
    if (response.status === 401) {
      const storedToken = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      const skipLogin = shouldSkipLoginRedirectForCalculator401();

      if (isApi401DebugEnabled()) {
        console.info(LOG_401, 'request', this.baseUrl + url, {
          skipLogin,
          hasToken: !!storedToken,
          hasRefresh: !!refreshToken,
          ...getAuth401DebugInfo(),
        });
      }

      if (skipLogin) {
        if (isApi401DebugEnabled()) {
          console.info(LOG_401, 'returning 401 (no redirect - public tools / embed)', url);
        }
        return response;
      }

      if (!storedToken && !refreshToken) {
        if (isApi401DebugEnabled()) {
          console.info(LOG_401, 'returning 401 (anonymous, no tokens)', url);
        }
        return response;
      }
      
      if (refreshToken) {
        try {
          const refreshResponse = await fetch(`${this.baseUrl}/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-application-type': 'bidmanager-admin-panel'
            },
            body: JSON.stringify({ refreshToken })
          });

          if (refreshResponse.ok) {
            const { token: newToken } = await refreshResponse.json();
            localStorage.setItem('token', newToken);
            
            // Retry original request with new token
            options.headers['Authorization'] = `Bearer ${newToken}`;
            response = await fetch(this.baseUrl + url, options);
          } else {
            // Refresh failed - logout user
            console.warn(LOG_401, 'redirect /login - refresh HTTP not ok', refreshResponse.status, url, getAuth401DebugInfo());
            localStorage.clear();
            window.location.href = '/login';
            throw new Error('Session expired. Please login again.');
          }
        } catch (error) {
          console.warn(LOG_401, 'redirect /login - refresh threw or network error', url, error?.message || error, getAuth401DebugInfo());
          localStorage.clear();
          window.location.href = '/login';
          throw error;
        }
      } else {
        console.warn(LOG_401, 'redirect /login - had token but no refreshToken', url, getAuth401DebugInfo());
        localStorage.clear();
        window.location.href = '/login';
        throw new Error('No authentication token');
      }
    }

    return response;
  }

  /**
   * Process API response and return structured data
   * Handles both success and error responses
   */
  async processResponse(response) {
    try {
      const data = await response.json();
      
      // If response is not OK but we got JSON data, return it (could contain error message)
      if (!response.ok) {
        // Return error response with backend message if available
        return {
          success: false,
          message: data.message || data.error || `Request failed with status ${response.status}`,
          error: data.error || data.message,
          ...data
        };
      }
      
      // For successful responses, ensure we have the standard format
      // Backend might return: { success, message, data } or just data directly
      if (data.success !== undefined) {
        // Backend already using standard format
        return data;
      } else {
        // Wrap response in standard format
        return {
          success: true,
          message: data.message || 'Operation completed successfully',
          data: data
        };
      }
    } catch (error) {
      // If response has no JSON body
      if (response.ok) {
        return {
          success: true,
          message: 'Operation completed successfully',
          data: null
        };
      } else {
        return {
          success: false,
          message: `Request failed with status ${response.status}`,
          error: response.statusText
        };
      }
    }
  }

  async createUser(userData) {
    try {
      const response = await this.apiRequest('/users/create', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async getUserById(id) {
    try {
      const response = await this.apiRequest(`/users/${id}`);

      if (!response.ok) {
        throw new Error(`Failed to get user: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  async updateUser(userId, userData) {
    try {
      const response = await this.apiRequest(`/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error updating user:', error.message);
      throw error;
    }
  }



  async deleteUser(userId) {
    try {
      const response = await this.apiRequest(`/users/${userId}`, {
        method: "DELETE",
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  }

  async loadAllLotEvents() {
    try {
      const response = await this.apiRequest('/bids/');

      if (!response.ok) {
        throw new Error(`Failed to load lot events: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading lot events:', error);
      throw error;
    }
  }

  async loadLotEvents(params = {}) {
    try {
      const { page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc', groupByLot = false } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      // Add groupByLot parameter if enabled
      if (groupByLot) {
        queryParams.append('groupByLot', 'true');
      }

      const response = await this.apiRequest(`/bids?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to load lot events: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading lot events:', error);
      throw error;
    }
  }

  async searchLotEvents(searchParams) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const response = await this.apiRequest(`/bids/search?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to search lot events: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching lot events:', error);
      throw error;
    }
  }

  // Get branch bids with optional grouping (OPTIMIZED)
  async getBranchBids(branchId, params = {}) {
    try {
      const { page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc', groupByLot = false } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      if (groupByLot) {
        queryParams.append('groupByLot', 'true');
      }

      const response = await this.apiRequest(`/bids/branch/${branchId}?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to load branch bids: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading branch bids:', error);
      throw error;
    }
  }

  // Get manager bids with optional grouping (OPTIMIZED)
  async getManagerBids(managerId, params = {}) {
    try {
      const { page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc', groupByLot = false } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      if (groupByLot) {
        queryParams.append('groupByLot', 'true');
      }

      const response = await this.apiRequest(`/bids/manager/${managerId}?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to load manager bids: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading manager bids:', error);
      throw error;
    }
  }

  // Get dealer bids with optional grouping (OPTIMIZED)
  async getDealerBids(dealerId, params = {}) {
    try {
      const { page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc', groupByLot = false } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      if (groupByLot) {
        queryParams.append('groupByLot', 'true');
      }

      const response = await this.apiRequest(`/bids/dealer/${dealerId}?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to load dealer bids: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading dealer bids:', error);
      throw error;
    }
  }

  // Get user/subdealer bids with optional grouping (OPTIMIZED)
  async getUserBids(userId, params = {}) {
    try {
      const { page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc', groupByLot = false } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      if (groupByLot) {
        queryParams.append('groupByLot', 'true');
      }

      const response = await this.apiRequest(`/bids/user/${userId}?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to load user bids: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading user bids:', error);
      throw error;
    }
  }

  // Get bid statistics by hierarchy (branch, manager, dealer, or user)
  async getBidStats(hierarchyType, hierarchyId) {
    try {
      const response = await this.apiRequest(`/bids/stats/${hierarchyType}/${hierarchyId}`);

      if (!response.ok) {
        throw new Error(`Failed to load bid stats: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading bid stats:', error);
      throw error;
    }
  }

  async loadAllUsers() {
    try {
      const response = await this.apiRequest('/users');

      if (!response.ok) {
        throw new Error(`Failed to load users: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading all users:', error);
      throw error;
    }
  }

  async loadUsers(params = {}) {
    try {
      const { page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc' } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      const response = await this.apiRequest(`/users?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to load users: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading users:', error);
      throw error;
    }
  }

  async searchUsers(searchParams) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const response = await this.apiRequest(`/users/search?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to search users: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  // Auction Accounts API Methods
  async getAuctionAccounts(params = {}) {
    try {
      const { page = 1, limit = 20, sortBy = 'created_at', sortOrder = 'desc' } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      const response = await this.apiRequest(`/auction-accounts?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to load auction accounts: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading auction accounts:', error);
      throw error;
    }
  }

  async searchAuctionAccounts(searchParams) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const response = await this.apiRequest(`/auction-accounts/search?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to search auction accounts: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching auction accounts:', error);
      throw error;
    }
  }

  async createAuctionAccount(accountData) {
    try {
      const response = await this.apiRequest('/auction-accounts/create', {
        method: 'POST',
        body: JSON.stringify(accountData),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error creating auction account:', error);
      throw error;
    }
  }

  async updateAuctionAccount(id, updateData) {
    try {
      const response = await this.apiRequest(`/auction-accounts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error updating auction account:', error);
      throw error;
    }
  }

  async deleteAuctionAccount(id) {
    try {
      const response = await this.apiRequest(`/auction-accounts/${id}`, {
        method: 'DELETE',
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error deleting auction account:', error);
      throw error;
    }
  }

  async getAuctionAccountsByUserId(userId) {
    try {
      const response = await this.apiRequest(`/auction-accounts/user/${userId}`);

      if (!response.ok) {
        throw new Error(`Failed to load user auction accounts: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading user auction accounts:', error);
      throw error;
    }
  }

  async getAuctionAccountStats() {
    try {
      const response = await this.apiRequest('/auction-accounts/stats');

      if (!response.ok) {
        throw new Error(`Failed to load auction account stats: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading auction account stats:', error);
      throw error;
    }
  }

  async getAvailableStates() {
    try {
      const response = await this.apiRequest('/auction-accounts/states');

      if (!response.ok) {
        throw new Error(`Failed to load available states: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading available states:', error);
      throw error;
    }
  }

  // NEW: User Management in Auction Accounts
  async getUsersForAccount(accountId, params = {}) {
    try {
      const { page = 1, limit = 20 } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      const response = await this.apiRequest(`/auction-accounts/${accountId}/users?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to load users for account: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading users for account:', error);
      throw error;
    }
  }

  async assignUserToAccount(accountId, userId) {
    try {
      const response = await this.apiRequest(`/auction-accounts/${accountId}/users`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      });

      if (!response.ok) {
        throw new Error(`Failed to assign user to account: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error assigning user to account:', error);
      throw error;
    }
  }

  async removeUserFromAccount(accountId, userId) {
    try {
      const response = await this.apiRequest(`/auction-accounts/${accountId}/users/${userId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to remove user from account: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing user from account:', error);
      throw error;
    }
  }

  async getAuctionAccountById(id) {
    try {
      const response = await this.apiRequest(`/auction-accounts/${id}`);

      if (!response.ok) {
        throw new Error(`Failed to load auction account: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading auction account:', error);
      throw error;
    }
  }

  // NEW: Dashboard API
  async getDashboard(role, userId, managerId, dealerId, branchId) {
    try {
      const params = new URLSearchParams({
        role,
        userId,
        ...(managerId && { managerId }),
        ...(dealerId && { dealerId }),
        ...(branchId && { branchId })
      });
      
      const response = await this.apiRequest(`/statistics/dashboard?${params}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load dashboard: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading dashboard:', error);
      throw error;
    }
  }

  // NEW: Branch API
  async getBranches(page = 1, limit = 100) {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      const response = await this.apiRequest(`/branches?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load branches: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading branches:', error);
      throw error;
    }
  }

  async getBranchById(id) {
    try {
      const response = await this.apiRequest(`/branches/${id}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load branch: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading branch:', error);
      throw error;
    }
  }

  async createBranch(branchData) {
    try {
      const response = await this.apiRequest('/branches', {
        method: 'POST',
        body: JSON.stringify(branchData),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error creating branch:', error);
      throw error;
    }
  }

  async updateBranch(branchId, branchData) {
    try {
      const response = await this.apiRequest(`/branches/${branchId}`, {
        method: 'PUT',
        body: JSON.stringify(branchData),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error updating branch:', error);
      throw error;
    }
  }

  async deleteBranch(branchId) {
    try {
      const response = await this.apiRequest(`/branches/${branchId}`, {
        method: 'DELETE',
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error deleting branch:', error);
      throw error;
    }
  }

  // NEW: Get Dealer's Subdealers
  async getDealerSubdealers(dealerId, page = 1, limit = 50) {
    try {
      const response = await this.apiRequest(`/users/dealer/${dealerId}/subdealers?page=${page}&limit=${limit}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load subdealers: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading subdealers:', error);
      throw error;
    }
  }

  // NEW: Get Users by Role
  async getUsersByRole(role, filters = {}, page = 1, limit = 20) {
    try {
      const params = new URLSearchParams({ ...filters, page, limit });
      const response = await this.apiRequest(`/users/role/${role}?${params}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load users by role: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading users by role:', error);
      throw error;
    }
  }

  // ============================================
  // PROFILE / SETTINGS API (Self-Service)
  // ============================================

  async getProfile() {
    try {
      const response = await this.apiRequest('/profile');
      
      if (!response.ok) {
        throw new Error(`Failed to load profile: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading profile:', error);
      throw error;
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await this.apiRequest('/profile', {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  async changePassword(currentPassword, newPassword) {
    try {
      const response = await this.apiRequest('/profile/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  }

  async updateAvatar(avatar) {
    try {
      const response = await this.apiRequest('/profile/avatar', {
        method: 'PUT',
        body: JSON.stringify({ avatar }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update avatar: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating avatar:', error);
      throw error;
    }
  }

  async updateDescription(description) {
    try {
      const response = await this.apiRequest('/profile/description', {
        method: 'PUT',
        body: JSON.stringify({ description }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update description: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating description:', error);
      throw error;
    }
  }

  async setLanguage(language) {
    try {
      const response = await this.apiRequest('/profile/language', {
        method: 'POST',
        body: JSON.stringify({ language }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to set language');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error setting language:', error);
      throw error;
    }
  }

  // ============================================
  // ROLE-BASED USER ENDPOINTS
  // ============================================

  async getSubdealers(page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc') {
    try {
      const response = await this.apiRequest(
        `/users/subdealers?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to load subdealers: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading subdealers:', error);
      throw error;
    }
  }

  async getDealers(page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc') {
    try {
      const response = await this.apiRequest(
        `/users/dealers?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to load dealers: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading dealers:', error);
      throw error;
    }
  }

  async getManagers(page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc') {
    try {
      const response = await this.apiRequest(
        `/users/managers?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to load managers: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading managers:', error);
      throw error;
    }
  }

  // ============================================
  // WINNING LOTS API
  // ============================================

  async getWinningLots(page = 1, limit = 10) {
    try {
      const response = await this.apiRequest(`/winning-lots?page=${page}&limit=${limit}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load winning lots: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading winning lots:', error);
      throw error;
    }
  }

  async getUserWinningBids(userId) {
    try {
      const response = await this.apiRequest(`/winning-lots/user/${userId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load user winning bids: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading user winning bids:', error);
      throw error;
    }
  }

  // ============================================
  // BRANCH STATISTICS
  // ============================================

  async getBranchStats(branchId) {
    try {
      const response = await this.apiRequest(`/branches/${branchId}/stats`);
      
      if (!response.ok) {
        throw new Error(`Failed to load branch stats: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading branch stats:', error);
      throw error;
    }
  }

  // ============================================
  // ROLE-SPECIFIC DASHBOARD ENDPOINTS
  // ============================================

  async getSuperAdminDashboard() {
    try {
      const response = await this.apiRequest('/statistics/super-admin');
      
      if (!response.ok) {
        throw new Error(`Failed to load super admin dashboard: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading super admin dashboard:', error);
      throw error;
    }
  }

  async getManagerDashboard(managerId, branchId) {
    try {
      const response = await this.apiRequest(`/statistics/manager/${managerId}/${branchId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load manager dashboard: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading manager dashboard:', error);
      throw error;
    }
  }

  async getDealerDashboard(dealerId, userId) {
    try {
      const response = await this.apiRequest(`/statistics/dealer/${dealerId}/${userId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load dealer dashboard: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading dealer dashboard:', error);
      throw error;
    }
  }

  async getSubdealerDashboard(userId) {
    try {
      const response = await this.apiRequest(`/statistics/subdealer/${userId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load subdealer dashboard: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading subdealer dashboard:', error);
      throw error;
    }
  }

  async updateStats(userId) {
    try {
      const response = await this.apiRequest(`/statistics/update/${userId}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update stats: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating stats:', error);
      throw error;
    }
  }

  async getDealersByBranch() {
    try {
      const response = await this.apiRequest('/statistics/dealers-by-branch');
      
      if (!response.ok) {
        throw new Error(`Failed to load dealers by branch: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading dealers by branch:', error);
      throw error;
    }
  }

  // ============================================
  // PURCHASED LOTS / WIN CARS API
  // ============================================

  async getPurchasedLots(params = {}) {
    try {
      const { 
        page = 1, 
        limit = 20, 
        sortBy = 'createdAt', 
        sortOrder = 'desc', 
        view = 'our_dealers',
        branchId, 
        managerId, 
        dealerId, 
        userId,
        quickSearch,
        lotNumber,
        vin,
        make,
        model,
        year,
        buyerNumber,
        location,
        price_min,
        price_max
      } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      // View: our_dealers (default when omitted) = only our registered dealers; all = all auction wins
      if (view === 'all') {
        queryParams.append('view', 'all');
      }

      // Add filter parameters if provided (according to guide)
      if (branchId !== undefined && branchId !== null && branchId !== '') {
        queryParams.append('branchId', branchId.toString());
      }
      if (managerId !== undefined && managerId !== null && managerId !== '') {
        queryParams.append('managerId', managerId.toString());
      }
      if (dealerId !== undefined && dealerId !== null && dealerId !== '') {
        queryParams.append('dealerId', dealerId.toString());
      }
      if (userId !== undefined && userId !== null && userId !== '') {
        queryParams.append('userId', userId.toString());
      }
      
      // Add search parameters if provided
      if (quickSearch !== undefined && quickSearch !== null && quickSearch !== '') {
        queryParams.append('quickSearch', quickSearch.toString());
      }
      if (lotNumber !== undefined && lotNumber !== null && lotNumber !== '') {
        queryParams.append('lotNumber', lotNumber.toString());
      }
      if (vin !== undefined && vin !== null && vin !== '') {
        queryParams.append('vin', vin.toString());
      }
      if (make !== undefined && make !== null && make !== '') {
        queryParams.append('make', make.toString());
      }
      if (model !== undefined && model !== null && model !== '') {
        queryParams.append('model', model.toString());
      }
      if (year !== undefined && year !== null && year !== '') {
        queryParams.append('year', year.toString());
      }
      if (buyerNumber !== undefined && buyerNumber !== null && buyerNumber !== '') {
        queryParams.append('buyerNumber', buyerNumber.toString());
      }
      if (location !== undefined && location !== null && location !== '') {
        queryParams.append('location', location.toString());
      }
      if (price_min !== undefined && price_min !== null && price_min !== '') {
        queryParams.append('price_min', price_min.toString());
      }
      if (price_max !== undefined && price_max !== null && price_max !== '') {
        queryParams.append('price_max', price_max.toString());
      }

      const response = await this.apiRequest(`/v3/purchased-lots?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load purchased lots: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading purchased lots:', error);
      throw error;
    }
  }

  async getMyPurchasedLots(params = {}) {
    try {
      const { page = 1, limit = 20, quickSearch } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      // Add search parameter if provided
      if (quickSearch !== undefined && quickSearch !== null && quickSearch !== '') {
        queryParams.append('quickSearch', quickSearch.toString());
      }

      const response = await this.apiRequest(`/v3/purchased-lots/my?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load my purchased lots: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading my purchased lots:', error);
      throw error;
    }
  }

  async getBranchPurchasedLots(branchId, params = {}) {
    try {
      const { page = 1, limit = 20 } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      const response = await this.apiRequest(`/v3/purchased-lots/branch/${branchId}?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load branch purchased lots: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading branch purchased lots:', error);
      throw error;
    }
  }

  async getDealerPurchasedLots(dealerId, params = {}) {
    try {
      const { page = 1, limit = 20 } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      const response = await this.apiRequest(`/v3/purchased-lots/dealer/${dealerId}?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load dealer purchased lots: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading dealer purchased lots:', error);
      throw error;
    }
  }

  async savePurchasedLot(lotData) {
    try {
      const response = await this.apiRequest('/v3/purchased-lots/save', {
        method: 'POST',
        body: JSON.stringify(lotData)
      });
      
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error saving purchased lot:', error);
      throw error;
    }
  }

  // Watchlist: get own watchlist (all roles)
  async getMyWatchlist() {
    try {
      const response = await this.apiRequest('/v3/watch-list/my');
      if (!response.ok) {
        throw new Error(`Failed to load watchlist: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error loading watchlist:', error);
      throw error;
    }
  }

  // Watchlist: get all with filters (super_admin only)
  async getWatchlistAdmin(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
      const url = queryParams.toString() ? `/v3/watch-list/admin?${queryParams}` : '/v3/watch-list/admin';
      const response = await this.apiRequest(url);
      if (!response.ok) {
        throw new Error(`Failed to load watchlist: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error loading admin watchlist:', error);
      throw error;
    }
  }

  // Watchlist: remove item (id or user_subbider_id + lot_number)
  async removeFromWatchlist(payload) {
    try {
      const response = await this.apiRequest('/v3/watch-list/delete', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || `Failed to remove: ${response.statusText}`);
      }
      return data;
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      throw error;
    }
  }

  async updatePurchasedLot(lotNumber, updateData) {
    try {
      const response = await this.apiRequest(`/v3/purchased-lots/${lotNumber}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error updating purchased lot:', error);
      throw error;
    }
  }

  /**
   * Verify email verification code
   * Note: This endpoint doesn't require authentication token
   * @param {string} userId - User ID from login response
   * @param {string} code - 6-digit verification code
   * @returns {Promise<Object>} Response with token and user data if successful
   */
  async verifyCode(userId, code) {
    try {
      const response = await fetch(`${this.baseUrl}/auth/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-application-type': 'bidmanager-admin-panel'
        },
        body: JSON.stringify({ userId, code }),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error verifying code:', error);
      throw error;
    }
  }

  /**
   * Resend verification code
   * Note: This endpoint doesn't require authentication token
   * @param {string} userId - User ID from login response
   * @returns {Promise<Object>} Response indicating success
   */
  async resendCode(userId) {
    try {
      const response = await fetch(`${this.baseUrl}/auth/resend-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-application-type': 'bidmanager-admin-panel'
        },
        body: JSON.stringify({ userId }),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error resending code:', error);
      throw error;
    }
  }

  /**
   * Upload images for a purchased lot
   * @param {string} lotNumber - Lot number
   * @param {FileList|File[]} files - Image files to upload (up to 10)
   * @returns {Promise<Object>} Response with uploaded image URLs
   */
  async uploadPurchasedLotImages(lotNumber, files) {
    try {
      const formData = new FormData();
      
      // Add files to FormData
      Array.from(files).forEach(file => {
        formData.append('images', file);
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`${this.baseUrl}/v3/purchased-lots/${lotNumber}/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-application-type': 'bidmanager-admin-panel'
        },
        body: formData,
      });

      return await this.processResponse(response);
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  }

  /**
   * Delete an image from a purchased lot
   * @param {string} lotNumber - Lot number
   * @param {string} imageKey - R2 storage key of the image to delete
   * @returns {Promise<Object>} Response indicating success
   */
  async deletePurchasedLotImage(lotNumber, imageKey) {
    try {
      const encodedKey = encodeURIComponent(imageKey);
      const response = await this.apiRequest(`/v3/purchased-lots/${lotNumber}/images/${encodedKey}`, {
        method: 'DELETE',
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }

  // ============================================
  // AUCTION FEES API
  // ============================================

  /**
   * Get auction fees with filters
   * @param {Object} params - Query parameters (country, auction, page, limit, etc.)
   * @returns {Promise<Object>} Response with auction fees and pagination
   */
  async getAuctionFees(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const response = await this.apiRequest(`/auction-fees?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load auction fees: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading auction fees:', error);
      throw error;
    }
  }

  /**
   * Get auction fee by ID
   * @param {number} id - Fee ID
   * @returns {Promise<Object>} Response with fee data
   */
  async getAuctionFeeById(id) {
    try {
      const response = await this.apiRequest(`/auction-fees/${id}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load auction fee: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading auction fee:', error);
      throw error;
    }
  }

  /**
   * Get fees by auction type and country
   * @param {string} auction - 'copart' or 'iaai'
   * @param {string} country - 'armenia' or 'georgia'
   * @returns {Promise<Object>} Response with fees
   */
  async getFeesByAuction(auction, country) {
    try {
      const response = await this.apiRequest(`/auction-fees/auction/${auction}?country=${country}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load fees by auction: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading fees by auction:', error);
      throw error;
    }
  }

  /**
   * Create new auction fee
   * @param {Object} feeData - Fee data object
   * @returns {Promise<Object>} Response with created fee
   */
  async createAuctionFee(feeData) {
    try {
      const response = await this.apiRequest('/auction-fees', {
        method: 'POST',
        body: JSON.stringify(feeData),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error creating auction fee:', error);
      throw error;
    }
  }

  /**
   * Update auction fee
   * @param {number} id - Fee ID
   * @param {Object} updateData - Update data object
   * @returns {Promise<Object>} Response with updated fee
   */
  async updateAuctionFee(id, updateData) {
    try {
      const response = await this.apiRequest(`/auction-fees/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error updating auction fee:', error);
      throw error;
    }
  }

  /**
   * Delete auction fee
   * @param {number} id - Fee ID
   * @returns {Promise<Object>} Response indicating success
   */
  async deleteAuctionFee(id) {
    try {
      const response = await this.apiRequest(`/auction-fees/${id}`, {
        method: 'DELETE',
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error deleting auction fee:', error);
      throw error;
    }
  }

  /**
   * Calculate fees for a specific price
   * @param {number} price - Vehicle price
   * @param {string} auction - 'copart' or 'iaai'
   * @param {string} country - 'armenia' or 'georgia'
   * @returns {Promise<Object>} Response with calculated fees
   */
  async calculateAuctionFees(price, auction, country) {
    try {
      const response = await this.apiRequest('/auction-fees/calculate', {
        method: 'POST',
        body: JSON.stringify({ price, auction, country }),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error calculating auction fees:', error);
      throw error;
    }
  }

  /**
   * Get auction fee statistics
   * @returns {Promise<Object>} Response with statistics
   */
  async getAuctionFeeStatistics() {
    try {
      const response = await this.apiRequest('/auction-fees/statistics');
      
      if (!response.ok) {
        throw new Error(`Failed to load auction fee statistics: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading auction fee statistics:', error);
      throw error;
    }
  }

  // ============================================
  // DELIVERY FEES API
  // ============================================

  /**
   * Get delivery fees with filters
   * @param {Object} params - Query parameters (country, auction, search, page, limit, etc.)
   * @returns {Promise<Object>} Response with delivery fees and pagination
   */
  async getDeliveryFees(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const response = await this.apiRequest(`/delivery-fees?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load delivery fees: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading delivery fees:', error);
      throw error;
    }
  }

  /**
   * Get delivery fee by ID
   * @param {number} id - Fee ID
   * @returns {Promise<Object>} Response with fee data
   */
  async getDeliveryFeeById(id) {
    try {
      const response = await this.apiRequest(`/delivery-fees/${id}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load delivery fee: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading delivery fee:', error);
      throw error;
    }
  }

  /**
   * Search delivery fees by keyword
   * @param {string} keyword - Search keyword
   * @param {string} auction - Optional auction filter
   * @param {string} country - Optional country filter
   * @returns {Promise<Object>} Response with matching fees
   */
  async searchDeliveryFees(keyword, auction, country) {
    try {
      const params = new URLSearchParams({ keyword });
      if (auction) params.append('auction', auction);
      if (country) params.append('country', country);

      const response = await this.apiRequest(`/delivery-fees/search?${params}`);
      
      if (!response.ok) {
        throw new Error(`Failed to search delivery fees: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error searching delivery fees:', error);
      throw error;
    }
  }

  /**
   * Get delivery fee statistics
   * @returns {Promise<Object>} Response with statistics by country and auction
   */
  async getDeliveryFeeStatistics() {
    try {
      const response = await this.apiRequest('/delivery-fees/statistics');
      
      if (!response.ok) {
        throw new Error(`Failed to load delivery fee statistics: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading delivery fee statistics:', error);
      throw error;
    }
  }

  /**
   * Create new delivery fee
   * @param {Object} feeData - Fee data object
   * @returns {Promise<Object>} Response with created fee
   */
  async createDeliveryFee(feeData) {
    try {
      const response = await this.apiRequest('/delivery-fees', {
        method: 'POST',
        body: JSON.stringify(feeData),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error creating delivery fee:', error);
      throw error;
    }
  }

  /**
   * Update delivery fee
   * @param {number} id - Fee ID
   * @param {Object} updateData - Update data object
   * @returns {Promise<Object>} Response with updated fee
   */
  async updateDeliveryFee(id, updateData) {
    try {
      const response = await this.apiRequest(`/delivery-fees/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error updating delivery fee:', error);
      throw error;
    }
  }

  /**
   * Delete delivery fee
   * @param {number} id - Fee ID
   * @returns {Promise<Object>} Response indicating success
   */
  async deleteDeliveryFee(id) {
    try {
      const response = await this.apiRequest(`/delivery-fees/${id}`, {
        method: 'DELETE',
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error deleting delivery fee:', error);
      throw error;
    }
  }

  /**
   * Get fees by auction type
   * @param {string} auction - 'copart' or 'iaai'
   * @param {string} country - Optional country filter
   * @returns {Promise<Object>} Response with fees
   */
  async getDeliveryFeesByAuction(auction, country) {
    try {
      const params = country ? `?country=${country}` : '';
      const response = await this.apiRequest(`/delivery-fees/auction/${auction}${params}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load fees by auction: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error loading fees by auction:', error);
      throw error;
    }
  }

  /**
   * Bulk create delivery fees
   * @param {Array} fees - Array of fee objects
   * @returns {Promise<Object>} Response indicating success
   */
  async bulkCreateDeliveryFees(fees) {
    try {
      const response = await this.apiRequest('/delivery-fees/bulk', {
        method: 'POST',
        body: JSON.stringify({ fees }),
      });
      return await this.processResponse(response);
    } catch (error) {
      console.error('Error bulk creating delivery fees:', error);
      throw error;
    }
  }

  // ============================================
  // CALCULATOR API
  // ============================================

  /**
   * Default import calculator country from server geo (client IP).
   * @returns {Promise<{ country: 'am'|'ge', source?: string }>}
   */
  async getCalculatorGeoCountry() {
    // Public GET: do not use apiRequest because stale Bearer tokens can trigger refresh redirects.
    try {
      const response = await fetch(`${this.baseUrl}/calculator/geo-country`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-application-type': 'bidmanager-admin-panel'
        },
      });
      if (!response.ok) {
        return { country: 'ge', source: 'fallback' };
      }
      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        return { country: 'ge', source: 'fallback' };
      }
      const country = data && data.country;
      if (country === 'am' || country === 'ge') {
        return { country, source: data.source };
      }
      return { country: 'ge', source: 'fallback' };
    } catch (error) {
      console.warn('getCalculatorGeoCountry:', error?.message || error);
      return { country: 'ge', source: 'fallback' };
    }
  }

  /**
   * Calculate vehicle import costs
   * @param {Object} params - Calculation parameters
   * @param {boolean | 'dashboard' | 'app'} fromSource - Request source for discount behavior
   * @returns {Promise<Object>} Calculation results
   */
  async calculatePrices(params, fromSource = false) {
    try {
      let from = null;
      if (fromSource === true || fromSource === 'dashboard') from = 'dashboard';
      else if (fromSource === 'app') from = 'app';

      const requestParams = {
        ...params,
        ...(from && { from }),
      };

      const response = await this.apiRequest('/calculator', {
        method: 'POST',
        body: JSON.stringify(requestParams),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Failed to calculate prices: ${response.statusText}`);
      }

      const data = await response.json();
      
      // API returns data directly (not wrapped in success/data structure)
      // Return as-is: { total, price, fob, transport, ... }
      return data;
    } catch (error) {
      console.error('Error calculating prices:', error);
      throw error;
    }
  }

  /**
   * Get locations for an auction
   * @param {number} auction - 1 for IAAI, 2 for Copart, 3 for Manheim
   * @param {string} country - 'am' or 'ge'
   * @param {boolean} includeFee - Include fee in response
   * @returns {Promise<Object>} Locations list
   */
  async getCalculatorLocations(auction, country = 'am', includeFee = false) {
    try {
      const params = new URLSearchParams({
        auction: auction.toString(),
        country,
      });
      if (includeFee) {
        params.append('fee', 'true');
      }

      const response = await this.apiRequest(`/calculator/locations?${params}`);

      if (!response.ok) {
        throw new Error(`Failed to load locations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading calculator locations:', error);
      throw error;
    }
  }

  /**
   * Get transport price for a location
   * @param {number} auction - 1 for IAAI, 2 for Copart, 3 for Manheim
   * @param {string} location - Location name or ID
   * @param {string} country - 'am' or 'ge'
   * @returns {Promise<Object>} Transport price and notes
   */
  async getTransportPrice(auction, location, country = 'am') {
    try {
      const params = new URLSearchParams({
        auction: auction.toString(),
        location,
        country,
      });

      const response = await this.apiRequest(`/calculator/transportprice?${params}`);

      if (!response.ok) {
        throw new Error(`Failed to load transport price: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading transport price:', error);
      throw error;
    }
  }

  /**
   * Get auction fee for a price (API: /calculator/fobs, response field `fob`)
   * @param {number} price - Vehicle price
   * @param {number} auction - 1 for IAAI, 2 for Copart, 3 for Manheim
   * @param {string} country - 'am' or 'ge'
   * @returns {Promise<Object>} Auction fee amount
   */
  async getFob(price, auction, country = 'am') {
    try {
      const params = new URLSearchParams({
        price: price.toString(),
        auction: auction.toString(),
        country,
      });

      const response = await this.apiRequest(`/calculator/fobs?${params}`);

      if (!response.ok) {
        throw new Error(`Failed to load auction fee: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading FOB:', error);
      throw error;
    }
  }

  /**
   * Georgia import calculator port destinations (public list for Tools/embed)
   */
  async getGeDestinations() {
    const response = await this.apiRequest('/calculator/ge-destinations', { method: 'GET' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Failed to load Georgia destinations: ${response.statusText}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  /**
   * Admin: all Georgia destinations (includes inactive)
   */
  async getGeDestinationsAll() {
    const response = await this.apiRequest('/calculator/ge-destinations/all', { method: 'GET' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Failed to load Georgia destinations: ${response.statusText}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  async createGeDestination(payload) {
    const response = await this.apiRequest('/calculator/ge-destinations', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || `Create failed: ${response.statusText}`);
    }
    return data;
  }

  async updateGeDestination(slug, payload) {
    const response = await this.apiRequest(`/calculator/ge-destinations/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || `Update failed: ${response.statusText}`);
    }
    return data;
  }

  async deleteGeDestination(slug, { permanent = false } = {}) {
    const q = permanent ? '?permanent=1' : '';
    const response = await this.apiRequest(
      `/calculator/ge-destinations/${encodeURIComponent(slug)}${q}`,
      { method: 'DELETE' }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || `Delete failed: ${response.statusText}`);
    }
    return data;
  }

  /**
   * Georgia import calculator vehicle types (per-type US transport add-on fee; public list for Tools)
   */
  async getGeVehicleTypes() {
    const response = await this.apiRequest('/calculator/ge-vehicle-types', { method: 'GET' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Failed to load Georgia vehicle types: ${response.statusText}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  async getGeVehicleTypesAll() {
    const response = await this.apiRequest('/calculator/ge-vehicle-types/all', { method: 'GET' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Failed to load Georgia vehicle types: ${response.statusText}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  async createGeVehicleType(payload) {
    const response = await this.apiRequest('/calculator/ge-vehicle-types', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || `Create failed: ${response.statusText}`);
    }
    return data;
  }

  async updateGeVehicleType(slug, payload) {
    const response = await this.apiRequest(`/calculator/ge-vehicle-types/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || `Update failed: ${response.statusText}`);
    }
    return data;
  }

  async deleteGeVehicleType(slug, { permanent = false } = {}) {
    const q = permanent ? '?permanent=1' : '';
    const response = await this.apiRequest(
      `/calculator/ge-vehicle-types/${encodeURIComponent(slug)}${q}`,
      { method: 'DELETE' }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || `Delete failed: ${response.statusText}`);
    }
    return data;
  }

  /**
   * Georgia import calculator engine types (optional US transport add-on; public list for Tools)
   */
  async getGeEngineTypes() {
    const response = await this.apiRequest('/calculator/ge-engine-types', { method: 'GET' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Failed to load Georgia engine types: ${response.statusText}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  async getGeEngineTypesAll() {
    const response = await this.apiRequest('/calculator/ge-engine-types/all', { method: 'GET' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Failed to load Georgia engine types: ${response.statusText}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  async createGeEngineType(payload) {
    const response = await this.apiRequest('/calculator/ge-engine-types', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || `Create failed: ${response.statusText}`);
    }
    return data;
  }

  async updateGeEngineType(slug, payload) {
    const response = await this.apiRequest(`/calculator/ge-engine-types/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || `Update failed: ${response.statusText}`);
    }
    return data;
  }

  async deleteGeEngineType(slug, { permanent = false } = {}) {
    const q = permanent ? '?permanent=1' : '';
    const response = await this.apiRequest(
      `/calculator/ge-engine-types/${encodeURIComponent(slug)}${q}`,
      { method: 'DELETE' }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || `Delete failed: ${response.statusText}`);
    }
    return data;
  }

  // ============================================
  // AUCTION ACCOUNT DATA (v2 endpoint)
  // ============================================

  /**
   * Get auction account data for a user (multiple accounts per platform)
   * This endpoint returns primary and secondary accounts for all platforms
   * @param {string} userUuid - User's first_uuid
   * @returns {Promise<Object>} Account data with copart_accounts, iaai_accounts, manheim_accounts arrays
   */
  async getAccData(userUuid) {
    try {
      const response = await this.apiRequest('/v2/acc_data', {
        method: 'POST',
        body: JSON.stringify({ first_uuid: userUuid }),
      });

      if (!response.ok) {
        throw new Error(`Failed to load account data: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading account data:', error);
      throw error;
    }
  }
}

const dashboardService = new DashboardService();

export default dashboardService;